import React, { useMemo } from 'react';

import { Role, UserProfile } from '@/features/auth/types';

import { useCurrentUser } from '@/hooks/useApiKey';

/**
 * Permission enum that mirrors the backend Permission enum
 * This should be kept in sync with the backend permissions.py file
 */
export enum Permission {
    // Document permissions
    ReadDocuments = 'documents:read',
    WriteDocuments = 'documents:write',
    DeleteDocuments = 'documents:delete',

    // User management permissions
    ManageUsers = 'users:manage',

    // Analytics permissions
    ViewAnalytics = 'analytics:view',

    // Project management permissions
    ManageProjects = 'projects:manage',

    // Conversation permissions
    ReadConversations = 'conversations:read',
    WriteConversations = 'conversations:write',

    // Team permissions
    ManageTeam = 'team:manage',
    ViewTeam = 'team:view',

    // Domain facts permissions
    ReadDomainFacts = 'domain-facts:read',
    WriteDomainFacts = 'domain-facts:write',
    DeleteDomainFacts = 'domain-facts:delete',

    // Component permissions
    ReadComponents = 'components:read',
    WriteComponents = 'components:write',

    // File management permissions
    ManageFiles = 'files:manage',

    // System administration permissions
    ManageSystem = 'system:manage',

    // End-of-Lease specific permissions
    ManageEndOfLease = 'end-of-lease:manage',

    // Aircraft permissions (shared between End of Lease and Maintenance Review)
    ReadAircrafts = 'aircrafts:read',
    WriteAircrafts = 'aircrafts:write',
    DeleteAircrafts = 'aircrafts:delete',

    // Maintenance Review permissions
    ReadMaintenanceReview = 'maintenance-review:read',
    WriteMaintenanceReview = 'maintenance-review:write',
    DeleteMaintenanceReview = 'maintenance-review:delete',

    // Asset 360 permissions
    ReadAsset360 = 'asset-360:read',
    WriteAsset360 = 'asset-360:write',
    DeleteAsset360 = 'asset-360:delete',
    OnboardingAsset360 = 'asset-360:onboarding',

    // Collection permissions
    ReadCollections = 'collections:read',
    WriteCollections = 'collections:write',
    DeleteCollections = 'collections:delete',

    // Document management permissions
    ManageDocument = 'documents:manage',

    // Miscellaneous permissions
    ReadFlags = 'flags:read',
    WriteFlags = 'flags:write',
    ManageFlags = 'flags:manage',
    ReadProducts = 'products:read',
    ReadProgress = 'progress:read',
    ManageAgents = 'agents:manage',

    // Compliance verification permissions
    ReadComplianceVerification = 'compliance-verification:read',
    WriteComplianceVerification = 'compliance-verification:write',

    // Ground Truth hub permissions
    // Intentionally defined ahead of use: the GT hub UI currently gates on a
    // feature flag, and this permission is the seam for the deferred swap to
    // permission-based gating. Keep it even though it isn't referenced yet.
    ManageGroundTruth = 'ground-truth:manage',
}

/**
 * Permission expression types for complex permission logic
 */
export type PermissionExpression =
    | Permission // Single permission
    | { and: PermissionExpression[] } // ALL permissions required (AND logic)
    | { or: PermissionExpression[] } // ANY permission required (OR logic)
    | Permission[]; // Array uses AND logic - user must have ALL permissions

/**
 * Evaluate a complex permission expression
 */
function evaluatePermissionExpression(
    userProfile: UserProfile | null | undefined,
    expression: PermissionExpression,
): boolean {
    if (!userProfile) return false;

    // Handle single permission
    if (typeof expression === 'string') {
        return (
            userProfile.permissions?.includes(expression as Permission) ?? false
        );
    }

    // Handle array (AND logic - user must have ALL permissions)
    if (Array.isArray(expression)) {
        return expression.every((perm) =>
            evaluatePermissionExpression(userProfile, perm),
        );
    }

    // Handle AND logic
    if ('and' in expression) {
        return expression.and.every((perm) =>
            evaluatePermissionExpression(userProfile, perm),
        );
    }

    // Handle OR logic
    if ('or' in expression) {
        return expression.or.some((perm) =>
            evaluatePermissionExpression(userProfile, perm),
        );
    }

    return false;
}

/**
 * Check if a user has all of the specified permissions
 * Uses AND logic - user needs ALL of the specified permissions
 */
function userHasAllPermissions(
    userProfile: UserProfile | null | undefined,
    permissions: Permission[],
): boolean {
    if (!userProfile) return false;

    return permissions.every((permission) => {
        return userProfile.permissions?.includes(permission) ?? false;
    });
}

/**
 * Check if a user has a specific role
 */
function userHasRole(
    userProfile: UserProfile | null | undefined,
    role: Role,
): boolean {
    return userProfile?.roles?.includes(role) ?? false;
}

/**
 * Permission check options for controlling component behavior
 */
export interface PermissionOptions {
    /** Hide the component completely if user lacks permissions */
    hide?: boolean;
    /** Disable the component if user lacks permissions */
    disable?: boolean;
    /** Fallback content to show when hidden (only used if hide=true) */
    fallback?: React.ReactNode;
}

/**
 * Result of permission check with visibility and disabled states
 */
export interface PermissionResult {
    /** Whether the user has the required permissions */
    hasPermission: boolean;
    /** Whether the component should be visible (opposite of should hide) */
    isVisible: boolean;
    /** Whether the component should be disabled */
    isDisabled: boolean;
    /** Fallback content to render when hidden */
    fallback?: React.ReactNode;
}

/**
 * Main permissions hook that provides permission checking functionality
 *
 * @example
 * ```tsx
 * // Basic permission check (single permission)
 * const { hasPermission } = usePermissions();
 * const canEdit = hasPermission(Permission.WriteDocuments);
 *
 * // AND logic (array syntax - user must have ALL permissions)
 * const canEdit = hasPermission([Permission.WriteDocuments, Permission.ManageFiles]);
 *
 * // AND logic (explicit object syntax)
 * const canEditAndManage = hasPermission({
 *   and: [Permission.WriteDocuments, Permission.ManageFiles]
 * });
 *
 * // OR logic (user needs ANY one of the permissions)
 * const canAccess = hasPermission({
 *   or: [Permission.DeleteDocuments, Permission.WriteDocuments]
 * });
 *
 * // Complex logic (OR with nested AND)
 * const canAccess = hasPermission({
 *   or: [
 *     Permission.DeleteDocuments,
 *     { and: [Permission.ReadDocuments, Permission.WriteDocuments] }
 *   ]
 * });
 *
 * // Component visibility control
 * const editButton = usePermissionCheck(
 *   { and: [Permission.WriteDocuments, Permission.ManageFiles] },
 *   { hide: true }
 * );
 * if (!editButton.isVisible) return editButton.fallback;
 *
 * // Array syntax (AND logic)
 * const deleteButton = usePermissionCheck([Permission.DeleteDocuments], { disable: true });
 * return <Button disabled={deleteButton.isDisabled}>Delete</Button>;
 * ```
 */
export function usePermissions() {
    const [userProfile] = useCurrentUser();

    /**
     * Check if the current user has the required permissions based on expression
     * Supports single permissions, arrays (AND logic), and complex expressions
     */
    const hasPermission = useMemo(
        () => (expression: PermissionExpression) => {
            return evaluatePermissionExpression(userProfile, expression);
        },
        [userProfile],
    );

    /**
     * Check if the current user has all of the specified permissions (Permission enum only)
     * Uses AND logic - user needs ALL of the specified permissions
     * @deprecated Use hasPermission with array syntax instead (now uses AND logic)
     */
    const hasAnyPermission = useMemo(
        () => (permissions: Permission[]) => {
            return evaluatePermissionExpression(userProfile, permissions);
        },
        [userProfile],
    );

    /**
     * Check if the current user has all of the specified permissions
     * Uses AND logic - user needs ALL of the specified permissions
     */
    const hasAllPermissions = useMemo(
        () => (permissions: Permission[]) => {
            return userHasAllPermissions(userProfile, permissions);
        },
        [userProfile],
    );

    /**
     * Check if the current user has a specific role
     */
    const hasRole = useMemo(
        () => (role: Role) => {
            return userProfile?.roles?.includes(role) ?? false;
        },
        [userProfile],
    );

    /**
     * Get all permissions for the current user
     */
    const userPermissions = useMemo(() => {
        if (!userProfile || !userProfile.permissions)
            return new Set<Permission>();
        return new Set(userProfile.permissions as Permission[]);
    }, [userProfile]);

    /**
     * Check permissions with component control options
     */
    const checkPermission = useMemo(
        () =>
            (
                expression: PermissionExpression,
                options: PermissionOptions = {},
            ): PermissionResult => {
                const hasAccess = evaluatePermissionExpression(
                    userProfile,
                    expression,
                );
                const shouldHide = options.hide && !hasAccess;
                const shouldDisable = options.disable && !hasAccess;

                return {
                    hasPermission: hasAccess,
                    isVisible: !shouldHide,
                    isDisabled: shouldDisable ?? false,
                    fallback: options.fallback,
                };
            },
        [userProfile],
    );

    return {
        /** Current user profile object with user data and permissions */
        userProfile,
        /** Check permissions using expression syntax (supports AND/OR logic) */
        hasPermission,
        /** Check if user has all of the specified permissions (deprecated - use hasPermission with array - now AND logic) */
        hasAnyPermission,
        /** Check if user has all of the specified permissions */
        hasAllPermissions,
        /** Check if user has a specific role */
        hasRole,
        /** All permissions available to the current user */
        userPermissions,
        /** Check permissions with component control options */
        checkPermission,
        /** Whether user is authenticated */
        isAuthenticated: !!userProfile,
        /** Current user's primary role (first role for backward compatibility) */
        userRole: userProfile?.roles?.[0],
    };
}

/**
 * Convenience hook for checking permission requirements with expression support
 * Supports single permissions, arrays (AND logic), and complex AND/OR expressions
 * Returns permission result with visibility and disabled state controls
 */
export function usePermissionCheck(
    expression: PermissionExpression,
    options: PermissionOptions = {},
): PermissionResult {
    const { checkPermission } = usePermissions();
    return checkPermission(expression, options);
}

/**
 * Legacy hook for checking permission requirements (Permission enum only)
 * @deprecated Use usePermissionCheck with new expression syntax instead
 * This mirrors the backend @permission decorator behavior which only accepts Permission enum
 * Returns permission result with visibility and disabled state controls
 *
 * @param permissions - Array of permissions (Permission enum only - mirrors backend)
 * @param options - Control options for component behavior
 */
export function usePermissionOnlyCheck(
    permissions: Permission[],
    options: PermissionOptions = {},
): PermissionResult {
    const [userProfile] = useCurrentUser();

    return useMemo(() => {
        const hasAccess = evaluatePermissionExpression(
            userProfile,
            permissions,
        );
        const shouldHide = options.hide && !hasAccess;
        const shouldDisable = options.disable && !hasAccess;

        return {
            hasPermission: hasAccess,
            isVisible: !shouldHide,
            isDisabled: shouldDisable ?? false,
            fallback: options.fallback,
        };
    }, [
        userProfile,
        permissions,
        options.hide,
        options.disable,
        options.fallback,
    ]);
}

/**
 * Higher-order component for permission-based rendering
 * Wraps components with permission checking logic using expression syntax
 */
export function withPermissions<T extends Record<string, any>>(
    Component: React.ComponentType<T>,
    expression: PermissionExpression,
    options: PermissionOptions = {},
) {
    return function PermissionWrappedComponent(props: T) {
        const permission = usePermissionCheck(expression, options);

        if (!permission.isVisible) {
            return permission.fallback || null;
        }

        const componentProps = {
            ...props,
            ...(permission.isDisabled && { disabled: true }),
        } as T;

        return React.createElement(Component, componentProps);
    };
}

/**
 * Permission-aware conditional rendering hook
 * Provides functions for easy conditional rendering based on permission expressions
 */
export function usePermissionRenderer() {
    const { hasPermission } = usePermissions();

    const when = useMemo(
        () =>
            (
                expression: PermissionExpression,
                render: () => React.ReactNode,
            ) => {
                return hasPermission(expression) ? render() : null;
            },
        [hasPermission],
    );

    const unless = useMemo(
        () =>
            (
                expression: PermissionExpression,
                render: () => React.ReactNode,
            ) => {
                return !hasPermission(expression) ? render() : null;
            },
        [hasPermission],
    );

    return { when, unless };
}

export default usePermissions;
